import { useEffect } from 'react';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { subscribeToChannel, type DuplexerEventName } from './duplexer-connection';
import { queryClient, queryKeys } from './query-client';

/**
 * ONE agent-task listener for the whole app, and it INVALIDATES rather than refetches.
 *
 * ## The defect this replaces
 *
 * `useAgentTaskDuplexer` registers a listener per hook INSTANCE (`useDuplexerSubscription` has no dedup),
 * and every task-backed hook's listener called `query.refetch()` on a SHARED React Query key. On the home
 * screen `useAgentWorkspaceTasks` alone mounts four times (the island badge directly, plus via
 * `useIslandState`, plus twice via `useIslandAgentStarted`), each fanning out over every workspace; the
 * quick-view and `useTasks` add their own on `queryKeys.tasks(undefined)`. One duplexer frame therefore ran
 * N refetches of the same cache entries inside a single `Set.forEach` tick — and `refetch()` defaults to
 * `cancelRefetch: true`, so they CANCEL AND RESTART each other instead of coalescing. Measured in
 * production over 2.5 days: 6,602 `runaway-fetch` reports, `key=account-wide`, `subs=8–23`, and
 * `calls=15` inside 89 ms. Not a poll — a synchronous storm, on `/api/tasks`, whose p999 is 6.4 s. That is
 * the "island loading for a long time" report.
 *
 * ## The shape of the fix
 *
 * One listener, ref-counted at module level, calling `queryClient.invalidateQueries` ONCE per event.
 * React Query then refetches each ACTIVE key exactly once, which is what a shared cache entry assumed all
 * along. Invalidate is strictly WEAKER than a forced refetch — it marks stale and refetches the observers
 * that exist, where `refetch()` also drives disabled/inactive queries — so the freshness contract the
 * push carries is preserved while the multiplier is gone.
 *
 * ## Why ref-counted here and not deduped inside `useDuplexerSubscription`
 *
 * Deduping in that hook was considered and REJECTED: its consumers pass genuinely different callbacks
 * (`lib/goals.ts` refetches a workspace's goals, `lib/use-goal-ledger.ts` one goal's ledger,
 * `lib/piled-up-jobs.ts` re-runs an imperative `load()`, `pages/apps.tsx` invalidates `workspace-apps`),
 * and a hook that collapsed them would silently drop real subscribers. The amplifier is not "many
 * listeners" — it is "many listeners doing the SAME work on a SHARED key", which is a property of the
 * task query layer, not of the transport.
 *
 * **What this covers**: every reader of `queryKeys.tasks(*)` and `queryKeys.workspaceTasks(*)` —
 * `useTasks`, `useWorkspaceTasks`, `useAgentWorkspaceTasks`, `useIslandQuickView`, and anything that
 * later renders from those keys, because it is the KEY that is invalidated, not a hook instance.
 * **What it does NOT cover**: the other `useAgentTaskDuplexer` consumers listed above, which each drive
 * their own distinct key and are one-per-screen rather than N-per-screen. They keep their own listener.
 */

const ENTITY_CHANGE_EVENTS: DuplexerEventName[] = ['created', 'updated', 'deleted'];

/**
 * Mark every task-backed cache entry stale, in two prefix sweeps: the account-wide + per-workspace
 * `tasks` scopes, and every `workspace-tasks` entry. Exported so a mutation path can reuse the exact
 * same set of keys the push invalidates — the two must never drift.
 */
export function invalidateAgentTaskQueries(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.tasksAllScopes });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceTasksAllScopes });
}

let subscriberCount = 0;
let disposeSubscription: (() => void) | null = null;

/**
 * Retain the shared listener; the returned disposer releases this holder's claim. The channel
 * subscription is opened on the FIRST retainer and dropped on the last release, so N mounted readers
 * still fan ONE event out to ONE invalidate.
 */
export function retainAgentTaskInvalidation(): () => void {
  subscriberCount += 1;
  if (subscriberCount === 1) {
    disposeSubscription = subscribeToChannel(IndividualsDuplexerChannel.AGENT_TASK, ENTITY_CHANGE_EVENTS, () =>
      invalidateAgentTaskQueries(),
    );
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    subscriberCount -= 1;
    if (subscriberCount === 0) {
      disposeSubscription?.();
      disposeSubscription = null;
    }
  };
}

/**
 * What a task-backed hook takes INSTEAD of its own `useAgentTaskDuplexer(() => refetch())`. Mounting it
 * from ten components costs one listener and one invalidate per event.
 *
 * HTTPS-only and client-only, matching `useDuplexerSubscription` — the shared duplexer connection cannot
 * be opened over plain http, and there is no socket under SSR.
 */
export function useAgentTaskInvalidation(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.location.protocol !== 'https:') return undefined;
    return retainAgentTaskInvalidation();
  }, []);
}

/** Test seam: how many holders the shared listener currently has. Always 0 or 1 subscription behind it. */
export function agentTaskInvalidationHolders(): number {
  return subscriberCount;
}
